import { inject, Injectable } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

import { AppStore } from '../../state/app.repository';
import { UserSettingsStore } from '../../state/user-settings.repository';
import { AppService } from './app.service';
import { LocalStreamService } from './local-stream.service';

/**
 * Room-level actions offered from the header (invite/share, leave). Shared by
 * the room info variants and the dock's Leave button so they stay identical.
 */
@Injectable({ providedIn: 'root' })
export class RoomActionsService {
  private readonly _userSettingsStore = inject(UserSettingsStore);
  private readonly _appService = inject(AppService);
  private readonly _appStore = inject(AppStore);
  private readonly _localStream = inject(LocalStreamService);
  private readonly _clipboard = inject(Clipboard);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _translateService = inject(TranslateService);

  copyInvite() {
    this._clipboard.copy(this._appService.inviteLink());
    this._snackBar.open(
      this._translateService.instant('A room invite link has been copied to your clipboard.'),
      undefined,
      {
        duration: 3000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
      }
    );
  }

  shareViaEmail() {
    const { roomName: room, userName: user } = this._userSettingsStore.value;
    const link = this._plainInviteLink();
    const subject = encodeURIComponent(`${user} invited you to a meeting: ${room}`);
    const body = encodeURIComponent(
      `${user} has invited you to join a meeting.\n\nRoom: ${room}\nJoin here: ${link}`
    );
    window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  }

  openCalendar() {
    const { roomName: room, userName: user } = this._userSettingsStore.value;
    const link = this._plainInviteLink();
    const now = new Date();
    const end = new Date(now.getTime() + 60 * 60 * 1000);

    const fmt = (d: Date) =>
      d
        .toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}/, '');

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `Meeting: ${room}`,
      dates: `${fmt(now)}/${fmt(end)}`,
      details: `${user} has invited you to join a meeting.\n\nJoin here: ${link}`,
    });

    window.open(`https://calendar.google.com/calendar/render?${params.toString()}`, '_blank');
  }

  leave() {
    this._appStore.update({
      loginAreaLogoSize: this._appService.getResponsiveLogoSize(),
      isConnectedView: false,
      activeSection: 'room-login',
    });
  }

  backToHome() {
    this._appStore.update({
      loginAreaLogoSize: this._appService.getResponsiveLogoSize(),
      activeSection: 'room-login',
    });
    this._localStream.stopCamStream('room-info back-home');
  }

  // Email/calendar invitations always use the plain room link: the sharer's
  // current breakout room is transient state that should not be baked into a
  // mail or a (possibly recurring) calendar event. The copy action keeps the
  // breakout-aware `inviteLink()` for the "pull someone in here right now"
  // use case.
  private _plainInviteLink(): string {
    return this._appService.buildInviteLink(this._userSettingsStore.value.roomId, {
      includeBreakoutRoom: false,
    });
  }
}
